"use client"

import { useState, useEffect, useRef, Fragment } from "react"
import { Dialog, Transition } from "@headlessui/react"
import {
  Send,
  Download,
  MessageSquare,
  AlertCircle,
  CheckCircle,
  Clock,
  XCircle,
  X,
  FileText,
  ImageIcon,
  RotateCw,
  RefreshCw,
  User,
  Calendar,
  Tag,
  Building,
  ChevronDown,
  ChevronUp,
  Eye,
  Lock,
} from "lucide-react"
import { InputCustom } from "../../components"
import InlineAttachmentManager from "./InlineAttachmentManager"
import toast from "react-hot-toast"
import ticketsService from "../../services/ticketsService"
import { useCurrentUser } from "../../utils/context/UserProvider";

export default function TicketDetailsModal({ isOpen, onClose, ticket, onTicketUpdated }) {
  const { currentUser } = useCurrentUser()
  const [messages, setMessages] = useState([])
  const [loading, setLoading] = useState(false)
  const [refreshing, setRefreshing] = useState(false)
  const [sending, setSending] = useState(false)
  const [newMessage, setNewMessage] = useState("")
  const [attachments, setAttachments] = useState([])
  const userRole = currentUser?.role?.name?.toLowerCase() || ""
  const [updatingStatus, setUpdatingStatus] = useState(false)
  const [resolutionNotes, setResolutionNotes] = useState("")
  const messagesEndRef = useRef(null)
  const [showTicketInfo, setShowTicketInfo] = useState(true)
  const [selectedImage, setSelectedImage] = useState(null)
  const [isImagePreviewOpen, setIsImagePreviewOpen] = useState(false)

  useEffect(() => {
    if (isOpen && ticket) {
      fetchTicketDetails()
    }
  }, [isOpen, ticket])

  useEffect(() => {
    scrollToBottom()
  }, [messages])

const fetchTicketDetails = async () => {
    try {
      setLoading(true)
      const response = await ticketsService.get(ticket._id)

      if (response.data) {
        setMessages(response.data.data.messages || [])
      }
    } catch (error) {
      console.error("Error fetching ticket details:", error)
      toast.error("Failed to load ticket details")
    } finally {
      setLoading(false)
    }
  }

  const refreshMessages = async () => {
    try {
      setRefreshing(true)
      const response = await ticketsService.get(ticket._id)

      if (response.data) {
        setMessages(response.data.data.messages || [])
        toast.success("Messages refreshed")
      }
    } catch (error) {
      console.error("Error refreshing messages:", error)
      toast.error("Failed to refresh messages")
    } finally {
      setRefreshing(false)
    }
  }

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" })
  }

  const sendMessage = async () => {
    if (!newMessage.trim() && attachments.length === 0) return

    try {
      setSending(true)

      // Prepare attachments data with base64
      const attachmentData = attachments
        .filter((file) => file.status === "completed")
        .map((file) => ({
          id: file.id,
          originalName: file.originalName,
          name: file.name,
          size: file.size,
          type: file.type,
          category: file.category,
          description: file.description,
          tags: file.tags,
          base64: file.base64, // Send base64 data
          thumbnail: file.thumbnail,
        }))

      const payload = {
        message: newMessage,
        attachments: attachmentData,
      }

      const response = await ticketsService.addMessage(ticket._id, payload)

      if (response.data) {
        setNewMessage("")
        setAttachments([])
        fetchTicketDetails()
        onTicketUpdated()
        toast.success("Message sent successfully")
      } else {
        throw new Error(response.data.message || "Failed to send message")
      }
    } catch (error) {
      console.error("Error sending message:", error)
      toast.error(error.response?.data?.message || error.message || "Failed to send message")
    } finally {
      setSending(false)
    }
  }

  const updateTicketStatus = async (newStatus) => {
    try {
      setUpdatingStatus(true)

      const response = await ticketsService.updateStatus(ticket._id, {
        status: newStatus,
        resolution_notes: resolutionNotes,
      })

      if (response.data && response.data.success) {
        const statusMessage =
          newStatus === "resolved"
            ? "Ticket resolved and conversations closed"
            : newStatus === "closed"
              ? "Ticket closed and conversations disabled"
              : `Ticket status updated to ${newStatus.replace("_", " ")}`

        toast.success(statusMessage)
        fetchTicketDetails()
        onTicketUpdated()
        setResolutionNotes("")
      } else {
        throw new Error(response.data.message || "Failed to update status")
      }
    } catch (error) {
      console.error("Error updating status:", error)
      toast.error(error.response?.data?.message || error.message || "Failed to update status")
    } finally {
      setUpdatingStatus(false)
    }
  }

  const getStatusColor = (status) => {
    const colorMap = {
      open: "bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300",
      in_progress: "bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300",
      waiting_client: "bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-300",
      waiting_admin: "bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-300",
      resolved: "bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300",
      closed: "bg-neutral-100 text-neutral-800 dark:border-neutral-900 dark:bg-neutral-900/30 dark:text-neutral-300",
    }
    return colorMap[status] || "bg-neutral-100 text-neutral-800 dark:border-neutral-900 dark:bg-neutral-900/30 dark:text-neutral-300"
  }

  const getPriorityColor = (priority) => {
    const colorMap = {
      low: "bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300",
      medium: "bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300",
      high: "bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-300",
      urgent: "bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300",
    }
    return colorMap[priority] || "bg-neutral-100 text-neutral-800 dark:border-neutral-900 dark:bg-neutral-900/30 dark:text-neutral-300"
  }

  const getStatusIcon = (status) => {
    switch (status) {
      case "open":
        return <AlertCircle className="h-4 w-4" />
      case "in_progress":
        return <Clock className="h-4 w-4" />
      case "resolved":
        return <CheckCircle className="h-4 w-4" />
      case "closed":
        return <XCircle className="h-4 w-4" />
      default:
        return <MessageSquare className="h-4 w-4" />
    }
  }

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    })
  }

  const formatChatTime = (dateString) => {
    const date = new Date(dateString)
    const now = new Date()
    const diffInHours = (now - date) / (1000 * 60 * 60)

    if (diffInHours < 24) {
      return date.toLocaleTimeString("en-GB", {
        hour: "2-digit",
        minute: "2-digit",
      })
    } else {
      return date.toLocaleDateString("en-GB", {
        day: "2-digit",
        month: "short",
        hour: "2-digit",
        minute: "2-digit",
      })
    }
  }

  const formatFileSize = (bytes) => {
    if (bytes === 0) return "0 Bytes"
    const k = 1024
    const sizes = ["Bytes", "KB", "MB", "GB"]
    const i = Math.floor(Math.log(bytes) / Math.log(k))
    return Number.parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i]
  }

  const getFileIcon = (fileType) => {
    if (fileType && fileType.startsWith("image/")) {
      return <ImageIcon className="h-4 w-4 text-blue-500" />
    }
    return <FileText className="h-4 w-4 text-neutral-500" />
  }

  const getAvatarColor = (name) => {
    const colors = [
      "bg-blue-500",
      "bg-green-500",
      "bg-purple-500",
      "bg-pink-500",
      "bg-indigo-500",
      "bg-yellow-500",
      "bg-red-500",
    ]
    const index = name?.charCodeAt(0) % colors.length || 0
    return colors[index]
  }

  const downloadFile = async (file) => {
    try {
      // For server-hosted files, use the file_path directly
      if (file.file_path) {
        const link = document.createElement("a")
        link.href = file.file_path
        link.download = file.original_name || file.originalName || "download"
        link.target = "_blank"
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
        toast.success("Download started")
        return
      }

      // Fallback for base64 data
      if (file.base64) {
        const base64Content = file.base64.split(",")[1] // Remove data:type;base64, prefix
        const byteCharacters = atob(base64Content)
        const byteNumbers = new Array(byteCharacters.length)

        for (let i = 0; i < byteCharacters.length; i++) {
          byteNumbers[i] = byteCharacters.charCodeAt(i)
        }

        const byteArray = new Uint8Array(byteNumbers)
        const mimeType = file.base64.split(",")[0].split(":")[1].split(";")[0]
        const blob = new Blob([byteArray], { type: mimeType })

        const url = window.URL.createObjectURL(blob)
        const link = document.createElement("a")
        link.href = url
        link.download = file.original_name || file.originalName || "download"
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
        window.URL.revokeObjectURL(url)

        toast.success("Download started")
        return
      }

      // Legacy server download
      if (file.filename) {
        const response = await ticketsService.download(file.filename)

        const url = window.URL.createObjectURL(new Blob([response.data]))
        const link = document.createElement("a")
        link.href = url
        link.download = file.original_name || file.originalName || "download"
        document.body.appendChild(link)
        link.click()
        link.remove()
        window.URL.revokeObjectURL(url)

        toast.success("Download started")
      }
    } catch (error) {
      console.error("Error downloading file:", error)
      toast.error("Failed to download file")
    }
  }

  const canUpdateStatus = userRole === "admin" || userRole === "superadmin"
  const isTicketClosed = ticket?.status === "closed" || ticket?.status === "resolved"

  const isImageFile = (fileType) => {
    return fileType && fileType.startsWith("image/")
  }

  const getImageUrl = (file) => {
    // Priority: file_path (CDN URL) > base64 > url
    if (file.file_path) {
      return file.file_path
    }
    if (file.base64) {
      return file.base64
    }
    if (file.url) {
      return file.url
    }
    return null
  }

  const openImagePreview = (file) => {
    setSelectedImage(file)
    setIsImagePreviewOpen(true)
  }

  const getFileName = (file) => {
    return file.original_name || file.originalName || file.name || "Unknown file"
  }

  const getFileSize = (file) => {
    return file.file_size || file.size || 0
  }

  const getFileType = (file) => {
    return file.file_type || file.type || "application/octet-stream"
  }

  if (!ticket) return null

  return (
    <>
      <Transition appear show={isOpen} as={Fragment}>
        <Dialog as="div" className="relative z-50" onClose={onClose}>
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-black/70 backdrop-blur-sm" />
          </Transition.Child>

          <div className="fixed inset-0 overflow-y-auto">
            <div className="flex min-h-full items-center justify-center p-4">
              <Transition.Child
                as={Fragment}
                enter="ease-out duration-300"
                enterFrom="opacity-0 scale-95"
                enterTo="opacity-100 scale-100"
                leave="ease-in duration-200"
                leaveFrom="opacity-100 scale-100"
                leaveTo="opacity-0 scale-95"
              >
                <Dialog.Panel className="w-full max-w-7xl transform overflow-hidden rounded-xl bg-white dark:bg-neutral-950 shadow-2xl transition-all max-h-[95vh] flex flex-col">
                  {/* Header */}
                  <div className="border-b border-neutral-200 dark:border-neutral-900 dark:bg-neutral-900/30 p-6 flex-shrink-0 bg-gradient-to-r from-neutral-50 to-white dark:from-neutral-800 dark:to-neutral-800">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-4">
                        <div className="flex items-center gap-3">
                          <div>
                            <Dialog.Title className="text-xl font-bold text-neutral-900 dark:text-white">
                              {ticket.ticket_id}
                            </Dialog.Title>
                            <p className="text-sm text-neutral-600 dark:text-neutral-300 mt-1">{ticket.title}</p>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <span
                            className={`inline-flex items-center px-3 py-1.5 rounded-full text-xs font-semibold ${getStatusColor(ticket.status)}`}
                          >
                            {getStatusIcon(ticket.status)}
                            <span className="ml-1.5 capitalize">{ticket.status.replace("_", " ")}</span>
                          </span>
                          <span
                            className={`inline-flex items-center px-3 py-1.5 rounded-full text-xs font-semibold ${getPriorityColor(ticket.priority)}`}
                          >
                            {ticket.priority.toUpperCase()}
                          </span>
                          {isTicketClosed && (
                            <span className="inline-flex items-center px-3 py-1.5 rounded-full text-xs font-semibold bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300">
                              <Lock className="h-3 w-3 mr-1" />
                              CONVERSATIONS CLOSED
                            </span>
                          )}
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <button
                          onClick={refreshMessages}
                          disabled={refreshing}
                          className="p-2 rounded-xl text-neutral-500 hover:bg-white/50 dark:text-neutral-400 dark:hover:bg-neutral-700 transition-colors"
                          type="button"
                          aria-label="Refresh messages"
                        >
                          <RefreshCw className={`h-5 w-5 ${refreshing ? "animate-spin" : ""}`} />
                        </button>
                        <button
                          onClick={onClose}
                          className="p-2 rounded-xl text-neutral-500 hover:bg-white/50 dark:text-neutral-400 dark:hover:bg-neutral-700 transition-colors"
                          type="button"
                          aria-label="Close"
                        >
                          <X size={20} />
                        </button>
                      </div>
                    </div>
                  </div>

                  <div className="flex-1 overflow-hidden flex">
                    {/* Main Chat Area */}
                    <div className="flex-1 flex flex-col min-w-0">
                      {/* Collapsible Ticket Info */}
                      <div className="border-b border-neutral-200 dark:border-neutral-900 dark:bg-neutral-900/30 flex-shrink-0">
                        <div className="p-3 bg-white dark:border-neutral-900 dark:bg-neutral-900/50">
                          <button
                            onClick={() => setShowTicketInfo(!showTicketInfo)}
                            className="flex items-center justify-between w-full  p-3 bg-white dark:border-neutral-900 dark:bg-neutral-900/30 rounded-xl border border-neutral-200 dark:border-neutral-900 dark:bg-neutral-900/30 shadow-sm hover:shadow-md transition-all"
                          >
                            <div className="flex items-center gap-3">
                              <div className="p-2 bg-blue-100 dark:bg-blue-900/30 rounded-xl">
                                <MessageSquare className="h-5 w-5 text-blue-600 dark:text-blue-400" />
                              </div>
                              <div className="text-left">
                                <h3 className="text-sm font-semibold text-neutral-900 dark:text-white">
                                  Ticket Information
                                </h3>
                                <p className="text-xs text-neutral-600 dark:text-neutral-400">
                                  {showTicketInfo ? "Click to hide details" : "Click to show details"}
                                </p>
                              </div>
                            </div>
                            {showTicketInfo ? (
                              <ChevronUp className="h-5 w-5 text-neutral-500 dark:text-neutral-400" />
                            ) : (
                              <ChevronDown className="h-5 w-5 text-neutral-500 dark:text-neutral-400" />
                            )}
                          </button>

                          <Transition
                            show={showTicketInfo}
                            enter="transition-all duration-300 ease-out"
                            enterFrom="opacity-0 max-h-0"
                            enterTo="opacity-100 max-h-96"
                            leave="transition-all duration-300 ease-in"
                            leaveFrom="opacity-100 max-h-96"
                            leaveTo="opacity-0 max-h-0"
                          >
                            <div className="overflow-hidden">
                              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 my-4">
                                <div className="bg-white dark:border-neutral-900 dark:bg-neutral-900/30 p-4 rounded-xl border border-neutral-200 dark:border-neutral-900 dark:bg-neutral-900/30 shadow-sm">
                                  <div className="flex items-center gap-3">
                                    <div className="p-2 bg-blue-100 dark:bg-blue-900/30 rounded-xl">
                                      <Building className="h-4 w-4 text-blue-600 dark:text-blue-400" />
                                    </div>
                                    <div>
                                      <p className="text-xs font-medium text-neutral-500 dark:text-neutral-400">
                                        Ad Account
                                      </p>
                                      <p className="text-sm font-semibold text-neutral-900 dark:text-white">
                                        {ticket.ad_account?.platform}
                                      </p>
                                      <p className="text-xs text-neutral-600 dark:text-neutral-300">
                                        {ticket.ad_account?.account_name}
                                      </p>
                                    </div>
                                  </div>
                                </div>

                                <div className="bg-white dark:border-neutral-900 dark:bg-neutral-900/30 p-4 rounded-xl border border-neutral-200 dark:border-neutral-900 dark:bg-neutral-900/30 shadow-sm">
                                  <div className="flex items-center gap-3">
                                    <div className="p-2 bg-green-100 dark:bg-green-900/30 rounded-xl">
                                      <User className="h-4 w-4 text-green-600 dark:text-green-400" />
                                    </div>
                                    <div>
                                      <p className="text-xs font-medium text-neutral-500 dark:text-neutral-400">
                                        Client
                                      </p>
                                      <p className="text-sm font-semibold text-neutral-900 dark:text-white">
                                        {ticket.client?.firstName} {ticket.client?.lastName}
                                      </p>
                                      <p className="text-xs text-neutral-600 dark:text-neutral-300">
                                        {ticket.client?.email}
                                      </p>
                                    </div>
                                  </div>
                                </div>

                                <div className="bg-white dark:border-neutral-900 dark:bg-neutral-900/30 p-4 rounded-xl border border-neutral-200 dark:border-neutral-900 dark:bg-neutral-900/30 shadow-sm">
                                  <div className="flex items-center gap-3">
                                    <div className="p-2 bg-purple-100 dark:bg-purple-900/30 rounded-xl">
                                      <Calendar className="h-4 w-4 text-purple-600 dark:text-purple-400" />
                                    </div>
                                    <div>
                                      <p className="text-xs font-medium text-neutral-500 dark:text-neutral-400">
                                        Created
                                      </p>
                                      <p className="text-sm font-semibold text-neutral-900 dark:text-white">
                                        {formatDate(ticket.createdAt)}
                                      </p>
                                      <p className="text-xs text-neutral-600 dark:text-neutral-300">
                                        {Math.ceil((new Date() - new Date(ticket.createdAt)) / (1000 * 60 * 60 * 24))}{" "}
                                        days ago
                                      </p>
                                    </div>
                                  </div>
                                </div>

                                <div className="bg-white dark:border-neutral-900 dark:bg-neutral-900/30 p-4 rounded-xl border border-neutral-200 dark:border-neutral-900 dark:bg-neutral-900/30 shadow-sm">
                                  <div className="flex items-center gap-3">
                                    <div className="p-2 bg-orange-100 dark:bg-orange-900/30 rounded-xl">
                                      <Tag className="h-4 w-4 text-orange-600 dark:text-orange-400" />
                                    </div>
                                    <div>
                                      <p className="text-xs font-medium text-neutral-500 dark:text-neutral-400">
                                        Reason
                                      </p>
                                      <p className="text-sm font-semibold text-neutral-900 dark:text-white capitalize">
                                        {ticket.reason?.replace("_", " ")}
                                      </p>
                                      <p className="text-xs text-neutral-600 dark:text-neutral-300">Support Category</p>
                                    </div>
                                  </div>
                                </div>
                              </div>

                              {ticket.description && (
                                <div className="bg-white dark:border-neutral-900 dark:bg-neutral-900/30 p-4 rounded-xl border border-neutral-200 dark:border-neutral-900 dark:bg-neutral-900/30 shadow-sm">
                                  <p className="text-xs font-medium text-neutral-500 dark:text-neutral-400 mb-2">
                                    Description
                                  </p>
                                  <p className="text-sm text-neutral-700 dark:text-neutral-300 leading-relaxed">
                                    {ticket.description}
                                  </p>
                                </div>
                              )}
                            </div>
                          </Transition>
                        </div>
                      </div>

                      {/* Chat Messages */}
                      <div className="flex-1 overflow-y-auto p-6 space-y-4 bg-neutral-50 dark:border-neutral-900 dark:bg-neutral-900/30">
                        {loading ? (
                          <div className="flex items-center justify-center py-12">
                            <div className="flex items-center gap-3">
                              <RotateCw size={24} className="animate-spin text-blue-600 dark:text-blue-400" />
                              <span className="text-neutral-600 dark:text-neutral-400">Loading messages...</span>
                            </div>
                          </div>
                        ) : messages.length === 0 ? (
                          <div className="text-center py-12">
                            <div className="p-4 bg-white dark:border-neutral-900 dark:bg-neutral-900/30 rounded-full w-16 h-16 mx-auto mb-4 flex items-center justify-center shadow-sm">
                              <MessageSquare className="h-8 w-8 text-neutral-400" />
                            </div>
                            <p className="text-neutral-600 dark:text-neutral-400 font-medium">No messages yet</p>
                            <p className="text-sm text-neutral-500 dark:text-neutral-500 mt-1">
                              {isTicketClosed
                                ? "This ticket is closed and conversations are disabled"
                                : "Start the conversation by sending a message"}
                            </p>
                          </div>
                        ) : (
                          messages.map((message, index) => {
                            const isClient = message.sender_role === "client"
                            const isSystem = message.message_type === "system"
                            const senderName = `${message.sender?.reference || message.sender?.fullName || ""}`

                            if (isSystem) {
                              return (
                                <div key={message._id} className="flex justify-center">
                                  <div className="bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-300 px-4 py-2 rounded-full text-sm font-medium border border-blue-200 dark:border-blue-800">
                                    {message.message}
                                  </div>
                                </div>
                              )
                            }

                            return (
                              <div key={message._id} className={`flex ${isClient ? "justify-end" : "justify-start"}`}>
                                <div className={`flex gap-3 max-w-[75%] ${isClient ? "flex-row-reverse" : "flex-row"}`}>
                                  {/* Avatar */}
                                  <div
                                    className={`w-10 h-10 rounded-full flex items-center justify-center text-white text-sm font-semibold flex-shrink-0 ${getAvatarColor(senderName)}`}
                                  >
                                    {senderName
                                      .split(" ")
                                      .map((n) => n[0])
                                      .join("")
                                      .toUpperCase() || "U"}
                                  </div>

                                  {/* Message Content */}
                                  <div className={`flex flex-col ${isClient ? "items-end" : "items-start"}`}>
                                    <div className="flex items-center gap-2 mb-1">
                                      <span className="text-sm font-semibold text-neutral-700 dark:text-neutral-300">
                                        {currentUser?.role?.name === 'Client' && !isClient ? 'Support' : senderName}
                                      </span>
                                      <span className="text-xs text-neutral-500 dark:text-neutral-400">
                                        {formatChatTime(message.createdAt)}
                                      </span>
                                    </div>

                                    <div
                                      className={`rounded-xl px-4 py-3 shadow-sm ${isClient
                                        ? "bg-blue-600 text-white rounded-br-md"
                                        : "bg-white dark:border-neutral-900 dark:bg-neutral-900/30 text-neutral-900 dark:text-white border border-neutral-200 dark:border-neutral-900 dark:bg-neutral-900/30 rounded-bl-md"
                                        }`}
                                    >
                                      <p className="text-sm leading-relaxed whitespace-pre-wrap">{message.message}</p>

                                      {message.attachments?.length > 0 && (
                                        <div className="mt-3 space-y-2">
                                          {message.attachments.map((file, fileIndex) => {
                                            const isImage = isImageFile(getFileType(file))
                                            const imageUrl = getImageUrl(file)
                                            const fileName = getFileName(file)
                                            const fileSize = getFileSize(file)

                                            if (isImage && imageUrl) {
                                              return (
                                                <div key={fileIndex} className="space-y-2">
                                                  <div
                                                    className={`relative rounded-xl overflow-hidden border-2 border-dashed cursor-pointer transition-all hover:border-solid ${isClient
                                                      ? "border-blue-300 hover:border-blue-400"
                                                      : "border-neutral-300 dark:border-neutral-900 dark:bg-neutral-900/30 hover:border-neutral-400 dark:hover:border-neutral-500"
                                                      }`}
                                                    onClick={() => openImagePreview(file)}
                                                  >
                                                    <img
                                                      src={imageUrl || "/placeholder.svg"}
                                                      alt={fileName}
                                                      className="w-full  h-auto max-h-48 object-cover rounded-xl"
                                                      onError={(e) => {
                                                        e.target.style.display = "none"
                                                        e.target.nextSibling.style.display = "flex"
                                                      }}
                                                    />
                                                    <div className="hidden items-center justify-center p-8 bg-neutral-100 dark:bg-neutral-700">
                                                      <div className="text-center">
                                                        <ImageIcon className="h-8 w-8 text-neutral-400 mx-auto mb-2" />
                                                        <p className="text-xs text-neutral-500">Failed to load image</p>
                                                      </div>
                                                    </div>
                                                    <div className="absolute inset-0 bg-black/0 hover:bg-black/10 transition-colors flex items-center justify-center opacity-0 hover:opacity-100">
                                                      <div className="bg-white/90 dark:border-neutral-900 dark:bg-neutral-900/90 rounded-full p-2">
                                                        <Eye className="h-4 w-4 text-neutral-700 dark:text-neutral-300" />
                                                      </div>
                                                    </div>
                                                  </div>
                                                  <div className="flex items-center justify-between">
                                                    <span className="text-xs text-neutral-400 dark:text-neutral-400 truncate">
                                                      {fileName}
                                                    </span>
                                                    <button
                                                      onClick={(e) => {
                                                        e.stopPropagation()
                                                        downloadFile(file)
                                                      }}
                                                      className={`p-1 rounded transition-colors ${isClient
                                                        ? "hover:bg-blue-500/20"
                                                        : "hover:bg-neutral-200 dark:hover:bg-neutral-600"
                                                        }`}
                                                    >
                                                      <Download className="h-3 w-3" />
                                                    </button>
                                                  </div>
                                                </div>
                                              )
                                            } else {
                                              return (
                                                <button
                                                  key={fileIndex}
                                                  onClick={() => downloadFile(file)}
                                                  className={`flex items-center gap-2 p-2 rounded-xl text-xs transition-colors ${isClient
                                                    ? "bg-blue-500/20 hover:bg-blue-500/30"
                                                    : "bg-neutral-100 dark:bg-neutral-700 hover:bg-neutral-200 dark:hover:bg-neutral-600"
                                                    }`}
                                                >
                                                  {getFileIcon(getFileType(file))}
                                                  <span className="flex-1 text-left truncate">{fileName}</span>
                                                  <span className="opacity-70">({formatFileSize(fileSize)})</span>
                                                  <Download className="h-3 w-3" />
                                                </button>
                                              )
                                            }
                                          })}
                                        </div>
                                      )}
                                    </div>
                                  </div>
                                </div>
                              </div>
                            )
                          })
                        )}
                        <div ref={messagesEndRef} />
                      </div>

                      {/* Message Input - Show disabled state when ticket is closed */}
                      {isTicketClosed ? (
                        <div className="p-6 border-t border-neutral-200 dark:border-neutral-900 dark:bg-neutral-900/30 bg-neutral-50 dark:border-neutral-900 dark:bg-neutral-900/50 flex-shrink-0">
                          <div className="flex items-center justify-center gap-3 p-4 bg-white dark:border-neutral-900 dark:bg-neutral-900/30 rounded-xl border border-neutral-200 dark:bg-neutral-800">
                            <Lock className="h-5 w-5 text-neutral-500 dark:text-neutral-400" />
                            <div className="text-center">
                              <p className="text-sm font-medium text-neutral-700 dark:text-neutral-300">
                                Conversations Closed
                              </p>
                              <p className="text-xs text-neutral-500 dark:text-neutral-400 mt-1">
                                This ticket has been {ticket.status} and no further messages can be sent
                              </p>
                            </div>
                          </div>
                        </div>
                      ) : (
                        <div className="p-6 border-t border-neutral-200 dark:border-neutral-900 dark:bg-neutral-900/30 bg-white dark:bg-neutral-950 flex-shrink-0">
                          <div className="flex gap-3 items-end">
                            <div className="flex-1">
                              <div className="relative">
                                <input
                                  value={newMessage}
                                  onChange={(e) => setNewMessage(e.target.value)}
                                  placeholder="Type your message..."
                                  onKeyDown={(e) => {
                                    if (e.key === "Enter" && !e.shiftKey) {
                                      e.preventDefault()
                                      sendMessage()
                                    }
                                  }}
                                  className="w-full px-4 py-3 pr-12 text-sm border border-neutral-300 dark:border-neutral-900 dark:bg-neutral-900/30 rounded-xl bg-white dark:border-neutral-900 dark:bg-neutral-900/30 text-neutral-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 focus:border-transparent transition-all placeholder-neutral-500 dark:placeholder-neutral-400"
                                />
                              </div>
                            </div>

                            {/* Inline Attachment Manager */}
                            <InlineAttachmentManager
                              files={attachments}
                              onFilesChange={setAttachments}
                              maxFiles={5}
                              maxFileSize={10 * 1024 * 1024} // 10MB
                              acceptedTypes={[
                                "image/*",
                                "application/pdf",
                                "application/msword",
                                "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
                                "text/plain",
                              ]}
                              showPreview={true}
                              uploadEndpoint="/api/tickets/upload"
                              downloadEndpoint="/api/tickets/download"
                              disabled={sending}
                            />

                            <button
                              onClick={sendMessage}
                              disabled={
                                sending ||
                                (!newMessage.trim() && attachments.filter((f) => f.status === "completed").length === 0)
                              }
                              className="p-3 w-11 h-11 flex justify-center items-center bg-blue-600 hover:bg-blue-700 text-white rounded-xl disabled:opacity-50 disabled:cursor-not-allowed transition-colors shadow-sm flex items-center gap-2"
                            >
                              {sending ? (
                                <RotateCw className="h-5 w-5 animate-spin" />
                              ) : (
                                <>
                                  <Send className="h-5 w-5" />
                                  {attachments.filter((f) => f.status === "completed").length > 0 && (
                                    <span className="text-xs bg-blue-500 px-2 py-1 rounded-full">
                                      {attachments.filter((f) => f.status === "completed").length}
                                    </span>
                                  )}
                                </>
                              )}
                            </button>
                          </div>

                          {/* Upload Progress Indicator */}
                          {attachments.some((f) => f.status === "uploading") && (
                            <div className="mt-3 p-3 bg-blue-50 dark:bg-blue-900/20 rounded-xl border border-blue-200 dark:border-blue-800">
                              <div className="flex items-center gap-2 text-sm text-blue-700 dark:text-blue-300">
                                <RotateCw className="h-4 w-4 animate-spin" />
                                <span>
                                  Uploading {attachments.filter((f) => f.status === "uploading").length} file(s)...
                                </span>
                              </div>
                            </div>
                          )}
                        </div>
                      )}
                    </div>

                    {/* Enhanced Admin Actions Sidebar */}
                    {canUpdateStatus && (
                      <div className="w-80 border-l border-neutral-200 dark:border-neutral-900 dark:bg-neutral-900/30 bg-white dark:bg-neutral-950 flex-shrink-0">
                        <div className="p-6">
                          <h3 className="text-lg font-bold text-neutral-900 dark:text-white mb-6 flex items-center gap-2">
                            <div className="p-1 bg-blue-100 dark:bg-blue-900/30 rounded">
                              <User className="h-4 w-4 text-blue-600 dark:text-blue-400" />
                            </div>
                            Admin Actions
                          </h3>

                          {!isTicketClosed ? (
                            <div className="space-y-6">
                              <div>
                                <label className="block text-sm font-semibold text-neutral-700 dark:text-neutral-300 mb-3">
                                  Update Status
                                </label>
                                <div className="grid grid-cols-1 gap-2">
                                  <button
                                    onClick={() => updateTicketStatus("in_progress")}
                                    disabled={updatingStatus || ticket.status === "in_progress"}
                                    className="px-4 py-3 text-sm border border-neutral-300 dark:border-neutral-900 dark:bg-neutral-900/30 rounded-xl bg-white dark:border-neutral-900 dark:bg-neutral-900/30 text-neutral-700 dark:text-neutral-300 hover:bg-neutral-50 dark:hover:bg-neutral-700 disabled:opacity-50 transition-colors"
                                  >
                                    In Progress
                                  </button>
                                  <button
                                    onClick={() => updateTicketStatus("waiting_client")}
                                    disabled={updatingStatus || ticket.status === "waiting_client"}
                                    className="px-4 py-3 text-sm border border-neutral-300 dark:border-neutral-900 dark:bg-neutral-900/30 rounded-xl bg-white dark:border-neutral-900 dark:bg-neutral-900/30 text-neutral-700 dark:text-neutral-300 hover:bg-neutral-50 dark:hover:bg-neutral-700 disabled:opacity-50 transition-colors"
                                  >
                                    Waiting for Client
                                  </button>
                                </div>
                              </div>

                              <div className="border-t border-neutral-200 dark:border-neutral-900 dark:bg-neutral-900/30 pt-6">
                                <InputCustom
                                  label="Resolution Notes"
                                  value={resolutionNotes}
                                  onChange={(value) => setResolutionNotes(value)}
                                  placeholder="Add resolution notes..."
                                  multiline
                                  rows={4}
                                />
                              </div>

                              <div className="grid grid-cols-1 gap-3">
                                <button
                                  onClick={() => updateTicketStatus("resolved")}
                                  disabled={updatingStatus}
                                  className="flex items-center justify-center gap-2 px-4 py-3 text-sm text-green-700 dark:text-green-300 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-xl hover:bg-green-100 dark:hover:bg-green-900/30 disabled:opacity-50 transition-colors"
                                >
                                  <CheckCircle className="h-4 w-4" />
                                  Mark as Resolved & Close Conversations
                                </button>
                                <button
                                  onClick={() => updateTicketStatus("closed")}
                                  disabled={updatingStatus}
                                  className="flex items-center justify-center gap-2 px-4 py-3 text-sm text-red-700 dark:text-red-300 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-xl hover:bg-red-100 dark:hover:bg-red-900/30 disabled:opacity-50 transition-colors"
                                >
                                  <XCircle className="h-4 w-4" />
                                  Close Ticket & Disable Conversations
                                </button>
                              </div>
                            </div>
                          ) : (
                            <div className="text-center py-8">
                              <div className="p-4 bg-neutral-100 dark:border-neutral-900 dark:bg-neutral-900/30 rounded-full w-16 h-16 mx-auto mb-4 flex items-center justify-center">
                                <XCircle className="h-8 w-8 text-neutral-400" />
                              </div>
                              <p className="text-sm text-neutral-600 dark:text-neutral-400 font-semibold mb-2">
                                Ticket {ticket.status === "resolved" ? "Resolved" : "Closed"}
                              </p>
                              <p className="text-xs text-neutral-500 dark:text-neutral-500">
                                This ticket has been {ticket.status} and conversations are disabled
                              </p>
                              {ticket.resolution_notes && (
                                <div className="mt-6 p-4 bg-neutral-50 dark:border-neutral-900 dark:bg-neutral-900/30 rounded-xl border border-neutral-200 dark:border-neutral-900 dark:bg-neutral-900/30">
                                  <p className="text-xs font-semibold text-neutral-700 dark:text-neutral-300 mb-2">
                                    Resolution Notes:
                                  </p>
                                  <p className="text-xs text-neutral-600 dark:text-neutral-400 leading-relaxed">
                                    {ticket.resolution_notes}
                                  </p>
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition>

      {/* Image Preview Modal */}
      {selectedImage && (
        <Transition appear show={isImagePreviewOpen} as={Fragment}>
          <Dialog as="div" className="relative z-[60]" onClose={() => setIsImagePreviewOpen(false)}>
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0"
              enterTo="opacity-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100"
              leaveTo="opacity-0"
            >
              <div className="fixed inset-0 bg-black/80 backdrop-blur-sm" />
            </Transition.Child>

            <div className="fixed inset-0 overflow-y-auto">
              <div className="flex min-h-full items-center justify-center p-4">
                <Transition.Child
                  as={Fragment}
                  enter="ease-out duration-300"
                  enterFrom="opacity-0 scale-95"
                  enterTo="opacity-100 scale-100"
                  leave="ease-in duration-200"
                  leaveFrom="opacity-100 scale-100"
                  leaveTo="opacity-0 scale-95"
                >
                  <Dialog.Panel className="w-full max-w-4xl transform overflow-hidden rounded-xl bg-white dark:bg-neutral-950 shadow-2xl transition-all">
                    <div className="relative">
                      <button
                        onClick={() => setIsImagePreviewOpen(false)}
                        className="absolute top-4 right-4 z-10 p-2 bg-black/50 hover:bg-black/70 text-white rounded-full transition-colors"
                      >
                        <X className="h-5 w-5" />
                      </button>
                      <img
                        src={getImageUrl(selectedImage) || "/placeholder.svg"}
                        alt={getFileName(selectedImage)}
                        className="w-full h-auto max-h-[80vh] object-contain"
                      />
                      <div className="p-6 border-t border-neutral-200 dark:border-neutral-900 dark:bg-neutral-900/30">
                        <div className="flex items-center justify-between">
                          <div>
                            <h3 className="text-lg font-semibold text-neutral-900 dark:text-white">
                              {getFileName(selectedImage)}
                            </h3>
                            <p className="text-sm text-neutral-600 dark:text-neutral-400">
                              {formatFileSize(getFileSize(selectedImage))} • {getFileType(selectedImage)}
                            </p>
                          </div>
                          <button
                            onClick={() => downloadFile(selectedImage)}
                            className="flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-xl transition-colors"
                          >
                            <Download className="h-4 w-4" />
                            Download
                          </button>
                        </div>
                      </div>
                    </div>
                  </Dialog.Panel>
                </Transition.Child>
              </div>
            </div>
          </Dialog>
        </Transition>
      )}
    </>
  )
}
